/**
 * Utility functions for handling @mentions in text content
 */

export interface MentionMatch {
  username: string;
  startIndex: number;
  endIndex: number;
  fullMatch: string;
}

/**
 * Extract all @mentions from text
 * Matches @username patterns (alphanumeric, underscores, hyphens)
 */
export function extractMentions(text: string): MentionMatch[] {
  if (!text) return [];
  
  // Match @username (username can contain letters, numbers, underscores, hyphens)
  const mentionRegex = /@([a-zA-Z0-9_-]+)/g;
  const mentions: MentionMatch[] = [];
  let match;
  
  while ((match = mentionRegex.exec(text)) !== null) {
    mentions.push({
      username: match[1],
      startIndex: match.index,
      endIndex: match.index + match[0].length,
      fullMatch: match[0]
    });
  }
  
  return mentions;
}

/**
 * Get unique usernames from mentions
 */
export function getUniqueMentionedUsernames(text: string): string[] {
  const mentions = extractMentions(text);
  const usernames = mentions.map(m => m.username.toLowerCase());
  return Array.from(new Set(usernames));
}

/**
 * Check if text contains any mentions
 */
export function hasMentions(text: string): boolean {
  return extractMentions(text).length > 0;
}

/**
 * Find the current mention being typed at cursor position
 * Returns null if not typing a mention
 */
export function getCurrentMention(text: string, cursorPosition: number): {
  username: string;
  startIndex: number;
} | null {
  if (!text || cursorPosition < 1) return null;
  
  // Look backwards from cursor to find @ symbol
  let startIndex = -1;
  for (let i = cursorPosition - 1; i >= 0; i--) {
    const char = text[i];
    
    // If we hit whitespace or newline before @, no mention
    if (char === ' ' || char === '\n' || char === '\r') {
      return null;
    }
    
    // Found the @ symbol
    if (char === '@') {
      startIndex = i;
      break;
    }
    
    // Invalid character for username
    if (!/[a-zA-Z0-9_-]/.test(char)) {
      return null;
    }
  }
  
  if (startIndex === -1) return null;
  
  // Extract the partial username
  const username = text.substring(startIndex + 1, cursorPosition);
  
  return {
    username,
    startIndex
  };
}

/**
 * Insert a completed mention into text at cursor position
 */
export function insertMention(
  text: string,
  cursorPosition: number,
  username: string
): {
  newText: string;
  newCursorPosition: number;
} {
  const currentMention = getCurrentMention(text, cursorPosition);
  
  if (!currentMention) {
    // Just insert @username at cursor
    const before = text.substring(0, cursorPosition);
    const after = text.substring(cursorPosition);
    const mention = `@${username} `;
    
    return {
      newText: before + mention + after,
      newCursorPosition: cursorPosition + mention.length
    };
  }
  
  // Replace the partial mention with the completed one
  const before = text.substring(0, currentMention.startIndex);
  const after = text.substring(cursorPosition);
  const mention = `@${username} `;
  
  return {
    newText: before + mention + after,
    newCursorPosition: currentMention.startIndex + mention.length
  };
}

/**
 * Highlight mentions in text for rendering
 * Returns array of text segments with mention info
 */
export interface TextSegment {
  text: string;
  isMention: boolean;
  username?: string;
}

export function parseTextWithMentions(text: string): TextSegment[] {
  if (!text) return [];
  
  const mentions = extractMentions(text);
  
  if (mentions.length === 0) {
    return [{ text, isMention: false }];
  }
  
  const segments: TextSegment[] = [];
  let lastIndex = 0;
  
  for (const mention of mentions) {
    // Add text before mention
    if (mention.startIndex > lastIndex) {
      segments.push({
        text: text.substring(lastIndex, mention.startIndex),
        isMention: false
      });
    }
    
    // Add mention
    segments.push({
      text: mention.fullMatch,
      isMention: true,
      username: mention.username
    });
    
    lastIndex = mention.endIndex;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    segments.push({
      text: text.substring(lastIndex),
      isMention: false
    });
  }
  
  return segments;
}
